import { useRouter } from 'next/router';
import { useTranslations } from 'next-intl';
import { useEffect, useId, useRef, useState, useSyncExternalStore } from 'react';
import styled, { createGlobalStyle, keyframes } from 'styled-components';

import { Button } from '@/components/ui';
import {
  acceptAllConsent,
  acknowledgeConsentNotice,
  COOKIE_SETTINGS_EVENT,
  closeConsentSettings,
  declineAllConsent,
  getConsentCategories,
  getConsentCountry,
  getConsentTier,
  hasGlobalPrivacyControl,
  isConsentBannerOpen,
  isConsentSettingsOpen,
  openConsentSettings,
  saveConsent,
  subscribeConsent,
} from '@/lib/consent';
import { isDappPath } from '@/lib/help-scout-beacon';
import { screens, up } from '@/themes';

export const COOKIE_POLICY_URL = 'https://www.bidali.com/legal/cookies';

/** Below the cart drawer (2000), above the page chrome and the chat pill. */
const CONSENT_Z_INDEX = 1500;

/**
 * Whether a consent category is on. Always `false` on the server and during
 * hydration (storage is only read in the browser), so markup never mismatches.
 */
export function useConsentCategory(category) {
  return useSyncExternalStore(
    subscribeConsent,
    () => getConsentCategories()[category],
    () => false
  );
}

const useConsentValue = (read, server) =>
  useSyncExternalStore(subscribeConsent, read, () => server);

const fadeUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }

  to {
    opacity: 1;
    transform: none;
  }
`;

/*
 * Fixed bottom container shared by the banner and the settings panel. It never
 * blocks the page: only the card takes clicks. Below 1024px the bottom padding
 * keeps the floating chat button clear (it sits higher on the dapp, above the
 * sticky bar).
 */
const Shell = styled.div`
  animation: ${fadeUp} 500ms cubic-bezier(0.22, 1, 0.36, 1) both;
  bottom: 0;
  left: 0;
  padding: 16px 16px
    calc(${({ $raised }) => ($raised ? 136 : 96)}px + env(safe-area-inset-bottom, 0px));
  pointer-events: none;
  position: fixed;
  right: 0;
  z-index: ${CONSENT_Z_INDEX};

  ${up.lg} {
    padding-bottom: 16px;
  }

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const Panel = styled.div`
  background: var(--card);
  border: 1px solid var(--border);
  border-radius: var(--radius-xl);
  box-shadow: var(--shadow-lg);
  color: var(--card-foreground);
  display: flex;
  flex-direction: column;
  font-family: var(--font-sans);
  gap: 16px;
  margin: 0 auto;
  max-height: calc(100vh - 32px);
  max-width: 768px;
  overflow-y: auto;
  padding: 16px;
  pointer-events: auto;
`;

/*
 * While the banner is up on a small screen, the page gets as much extra room
 * at the bottom as the banner takes, so whatever sits under it (the checkout's
 * primary action, the last line of a page) can always be scrolled clear.
 */
const PageInset = createGlobalStyle`
  @media (max-width: ${screens.lg - 1}px) {
    body {
      padding-bottom: ${({ $height }) => $height}px;
    }
  }
`;

const ConsentButton = styled(Button).attrs({ $size: 'lg', type: 'button' })``;

const Row = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;

  ${up.sm} {
    align-items: center;
    flex-direction: row;
  }
`;

const Message = styled.p`
  color: var(--muted-foreground);
  flex: 1;
  font-size: 14px;
  line-height: 24px;
  margin: 0;
`;

const Actions = styled.div`
  display: flex;
  flex-shrink: 0;
  flex-wrap: wrap;
  gap: 8px;
`;

const EndActions = styled(Actions)`
  justify-content: flex-end;
`;

const PolicyAnchor = styled.a`
  color: var(--foreground);
  text-decoration: underline;
  text-underline-offset: 2px;

  &:focus-visible {
    border-radius: 2px;
    outline: 2px solid var(--ring);
    outline-offset: 2px;
  }
`;

const Heading = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const Title = styled.p`
  color: var(--foreground);
  font-size: 14px;
  font-weight: 600;
  line-height: 20px;
  margin: 0;
`;

const Toggle = styled.div`
  align-items: flex-start;
  display: flex;
  gap: 12px;
`;

const Switch = styled.input`
  accent-color: var(--primary);
  cursor: pointer;
  flex-shrink: 0;
  height: 16px;
  margin: 2px 0 0;
  width: 16px;
`;

const ToggleText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const ToggleLabel = styled.label`
  color: var(--foreground);
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
`;

const ToggleDescription = styled.p`
  color: var(--muted-foreground);
  font-size: 14px;
  line-height: 20px;
  margin: 0;
`;

function ConsentShell({ label, children }) {
  const { pathname } = useRouter();
  const shell = useRef(null);
  const [height, setHeight] = useState(0);

  useEffect(() => {
    const node = shell.current;
    if (!node) return undefined;

    const measure = () => setHeight(Math.ceil(node.getBoundingClientRect().height));
    measure();

    if (typeof ResizeObserver !== 'function') return undefined;
    const observer = new ResizeObserver(measure);
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  // The embedded dapp keeps its own layout untouched (no extra scroll room).
  const dapp = isDappPath(pathname);

  return (
    <Shell $raised={dapp} aria-label={label} ref={shell} role="region">
      {height > 0 && !dapp && <PageInset $height={height} />}
      <Panel>{children}</Panel>
    </Shell>
  );
}

function PolicyLink() {
  const t = useTranslations('consent');

  return (
    <PolicyAnchor href={COOKIE_POLICY_URL} rel="noopener noreferrer" target="_blank">
      {t('policyLink')}
    </PolicyAnchor>
  );
}

/**
 * Bottom-of-viewport cookie banner. Not an interstitial: it never blocks the
 * page, and it only renders in the browser once the visitor's region is
 * known. Strict regions get Accept / Decline; elsewhere it is a notice with
 * OK / Opt out (analytics, and ads outside Canada, already apply).
 */
export function CookieBanner() {
  const t = useTranslations('consent');
  const open = useConsentValue(isConsentBannerOpen, false);
  const tier = useConsentValue(getConsentTier, 'unknown');
  const country = useConsentValue(getConsentCountry, null);

  if (!open) return null;

  const notice = tier === 'notice';
  const message = notice
    ? country === 'CA'
      ? t('noticeMessageCa')
      : t('noticeMessage')
    : t('message');

  return (
    <ConsentShell label={t('label')}>
      <Row>
        <Message>
          {message} <PolicyLink />
        </Message>
        <Actions>
          <ConsentButton $variant="ghost" onClick={openConsentSettings}>
            {t('manage')}
          </ConsentButton>
          {notice ? (
            <>
              <ConsentButton $variant="outline" onClick={declineAllConsent}>
                {t('optOut')}
              </ConsentButton>
              <ConsentButton onClick={acknowledgeConsentNotice}>{t('ok')}</ConsentButton>
            </>
          ) : (
            <>
              <ConsentButton $variant="outline" onClick={declineAllConsent}>
                {t('decline')}
              </ConsentButton>
              <ConsentButton onClick={acceptAllConsent}>{t('accept')}</ConsentButton>
            </>
          )}
        </Actions>
      </Row>
    </ConsentShell>
  );
}

function CategoryToggle({ label, description, checked, onChange }) {
  const id = useId();

  return (
    <Toggle>
      <Switch
        aria-checked={checked}
        aria-describedby={`${id}-description`}
        checked={checked}
        id={id}
        onChange={(event) => onChange(event.target.checked)}
        role="switch"
        type="checkbox"
      />
      <ToggleText>
        <ToggleLabel htmlFor={id}>{label}</ToggleLabel>
        <ToggleDescription id={`${id}-description`}>{description}</ToggleDescription>
      </ToggleText>
    </Toggle>
  );
}

/** Per-category toggles; mounted fresh each time settings open. */
function SettingsForm() {
  const t = useTranslations('consent');
  const [choice, setChoice] = useState(getConsentCategories);

  return (
    <>
      <Heading>
        <Title>{t('settings')}</Title>
        <Message>
          {t('settingsDescription')} <PolicyLink />
        </Message>
      </Heading>
      <CategoryToggle
        checked={choice.analytics}
        description={t('analyticsDescription')}
        label={t('analyticsLabel')}
        onChange={(analytics) => setChoice((current) => ({ ...current, analytics }))}
      />
      <CategoryToggle
        checked={choice.ads}
        description={t('adsDescription')}
        label={t('adsLabel')}
        onChange={(ads) => setChoice((current) => ({ ...current, ads }))}
      />
      <EndActions>
        <ConsentButton $variant="outline" onClick={closeConsentSettings}>
          {t('dismiss')}
        </ConsentButton>
        <ConsentButton onClick={() => saveConsent(choice)}>{t('save')}</ConsentButton>
      </EndActions>
    </>
  );
}

/**
 * "Cookie settings" panel: an Analytics and an Advertising toggle in every
 * tier. With Global Privacy Control on it explains the signal instead.
 */
export function CookieSettingsPanel() {
  const t = useTranslations('consent');
  const open = useConsentValue(isConsentSettingsOpen, false);

  if (!open) return null;

  if (hasGlobalPrivacyControl()) {
    return (
      <ConsentShell label={t('settings')}>
        <Row>
          <Message>
            {t('gpcNotice')} <PolicyLink />
          </Message>
          <Actions>
            <ConsentButton onClick={closeConsentSettings}>{t('dismiss')}</ConsentButton>
          </Actions>
        </Row>
      </ConsentShell>
    );
  }

  return (
    <ConsentShell label={t('settings')}>
      <SettingsForm />
    </ConsentShell>
  );
}

/**
 * The consent UI: the banner, and the settings panel in the same shell. The
 * panel opens from the banner's "Settings" button and from the footer's
 * "Cookie settings" button (`window` event `bidali:open-cookie-settings`).
 */
export default function CookieConsent() {
  useEffect(() => {
    window.addEventListener(COOKIE_SETTINGS_EVENT, openConsentSettings);
    return () => window.removeEventListener(COOKIE_SETTINGS_EVENT, openConsentSettings);
  }, []);

  return (
    <>
      <CookieBanner />
      <CookieSettingsPanel />
    </>
  );
}
